"use client"

import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import type { CSSProperties } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import CartButton from "@modules/layout/components/cart-button"
import SideMenu, {
  NavCategory,
  NavMenuLink,
  SideMenuTheme,
} from "@modules/layout/components/side-menu"
import StorefrontLogo from "@modules/layout/components/storefront-logo"
import SearchBar from "@modules/search/components/search-bar"
import { MagnifyingGlass, ShoppingBag } from "@medusajs/icons"
import { NavigationMenu, NavigationMenuItem } from "@lib/data/navigation"
import type { StorefrontConfig } from "@lib/data/store-config"
import type { AnnouncementBar } from "@lib/data/announcement-bar"
import type { StorefrontComponents } from "@lib/data/storefront-components"
import { getStoreWhatsappUrl } from "@lib/util/env"
import { getStorefrontLogoDimensions } from "@lib/util/storefront-logo-dimensions"
import MarqueeText from "react-marquee-text"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"

type NavProps = {
  menu: NavigationMenu
  storeConfig: StorefrontConfig
  announcementBar: AnnouncementBar
  storefrontComponents: StorefrontComponents
}

const announcementMessageSchema = {
  ...defaultSchema,
  tagNames: Array.from(new Set([...(defaultSchema.tagNames ?? []), "ins"])),
}

function AnnouncementMessageContent({ message }: { message: string }) {
  return (
    <span className="announcement-message-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, announcementMessageSchema]]}
        components={{
          p: ({ children }) => <span>{children}</span>,
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          ins: ({ children }) => <ins className="underline">{children}</ins>,
        }}
      >
        {message}
      </ReactMarkdown>
    </span>
  )
}

const FALLBACK_NAV_CATEGORIES: NavCategory[] = [
  { id: "shop", name: "Tienda", href: "/store" },
  { id: "about", name: "Sobre Nosotros", href: "/about" },
  {
    id: "contact",
    name: "Contáctanos",
    href: getStoreWhatsappUrl(),
    isExternal: true,
  },
]

const toTestId = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]/g, "")

const isExternalHref = (href: string) => /^https?:\/\//i.test(href)

const HEX_COLOR_REGEX = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const RGB_COLOR_REGEX = /^rgba?\(([^)]+)\)$/i

const clampChannel = (value: number) => {
  if (!Number.isFinite(value)) return 0
  return Math.min(255, Math.max(0, Math.round(value)))
}

const toOpaqueRgba = (red: number, green: number, blue: number) =>
  `rgba(${clampChannel(red)}, ${clampChannel(green)}, ${clampChannel(blue)}, 1)`

const toOpaqueHexColor = (value: string): string | null => {
  const match = value.trim().match(HEX_COLOR_REGEX)
  if (!match) return null

  const hex = match[1]

  if (hex.length === 3 || hex.length === 4) {
    const [r, g, b] = hex.split("")
    return toOpaqueRgba(
      Number.parseInt(`${r}${r}`, 16),
      Number.parseInt(`${g}${g}`, 16),
      Number.parseInt(`${b}${b}`, 16)
    )
  }

  if (hex.length === 6 || hex.length === 8) {
    return toOpaqueRgba(
      Number.parseInt(hex.slice(0, 2), 16),
      Number.parseInt(hex.slice(2, 4), 16),
      Number.parseInt(hex.slice(4, 6), 16)
    )
  }

  return null
}

const toOpaqueRgbColor = (value: string): string | null => {
  const match = value.trim().match(RGB_COLOR_REGEX)
  if (!match) return null

  const parts = match[1]
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)

  if (parts.length < 3) return null

  const red = Number.parseFloat(parts[0])
  const green = Number.parseFloat(parts[1])
  const blue = Number.parseFloat(parts[2])

  if (![red, green, blue].every((channel) => Number.isFinite(channel))) {
    return null
  }

  return toOpaqueRgba(red, green, blue)
}

const toOpaqueColor = (value: string) =>
  toOpaqueHexColor(value) ?? toOpaqueRgbColor(value) ?? value

const mapMenuItemToNavLink = (
  item: NavigationMenuItem,
  depth = 0
): NavMenuLink | null => {
  const name = item.title.trim()
  if (!name) return null

  const children = item.children
    .map((child) => mapMenuItemToNavLink(child, depth + 1))
    .filter((child): child is NavMenuLink => Boolean(child))

  const href = item.href ?? (children.length ? "#" : null)
  if (!href) return null

  return {
    id: item.itemId,
    name,
    href,
    isExternal: isExternalHref(href),
    customColor: item.customColor,
    parentEffectClass: depth === 0 ? item.parentEffectClass : null,
    children: children.length ? children : undefined,
  }
}

const renderMenuLink = (
  item: NavMenuLink,
  {
    className,
    testId,
    depth,
    onClick,
    onFocus,
    onBlur,
    style,
  }: {
    className: string
    testId: string
    depth: number
    onClick?: () => void
    onFocus?: () => void
    onBlur?: () => void
    style?: CSSProperties
  }
) => {
  const linkStyle = item.customColor
    ? ({ ...(style ?? {}), color: item.customColor } as CSSProperties)
    : style
  const effectClassName =
    depth === 0 && item.parentEffectClass ? ` ${item.parentEffectClass}` : ""
  const resolvedClassName = `${className}${effectClassName}`

  if (item.isExternal) {
    return (
      <a
        href={item.href}
        className={resolvedClassName}
        style={linkStyle}
        data-testid={testId}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {item.name}
      </a>
    )
  }

  return (
    <LocalizedClientLink
      href={item.href}
      prefetch
      className={resolvedClassName}
      style={linkStyle}
      data-testid={testId}
      onClick={onClick}
      target={item.target}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {item.name}
    </LocalizedClientLink>
  )
}

export default function Nav({
  menu,
  storeConfig,
  announcementBar,
  storefrontComponents,
}: NavProps) {
  const navbarSettings = storefrontComponents?.navbar
  const logoPosition =
    navbarSettings?.logoPosition === "center" ? "center" : "left"
  const isFixed = navbarSettings?.position === "fixed"
  const navbarHeightPx = Math.min(
    120,
    Math.max(
      56,
      typeof navbarSettings?.heightPx === "number" &&
        Number.isFinite(navbarSettings.heightPx)
        ? Math.round(navbarSettings.heightPx)
        : 74
      )
  )
  const logoDimensions = useMemo(
    () => getStorefrontLogoDimensions(navbarSettings?.logoWidthPx),
    [navbarSettings?.logoWidthPx]
  )
  const configuredNavbarBackgroundColor =
    navbarSettings?.backgroundColor || "rgba(255, 255, 255, 1)"
  const solidNavbarBackgroundColor = useMemo(
    () => toOpaqueColor(configuredNavbarBackgroundColor),
    [configuredNavbarBackgroundColor]
  )
  const [hasScrolledPastTop, setHasScrolledPastTop] = useState(false)
  const effectiveNavbarBackgroundColor =
    isFixed && hasScrolledPastTop
      ? solidNavbarBackgroundColor
      : configuredNavbarBackgroundColor

  const announcementBarStyle = useMemo(
    () => ({
      backgroundColor:
        announcementBar?.backgroundColor?.trim() || "rgba(0, 0, 0, 1)",
      color: announcementBar?.textColor?.trim() || "rgba(255, 255, 255, 1)",
    }),
    [announcementBar?.backgroundColor, announcementBar?.textColor]
  )

  const navStyles = useMemo(
    () =>
      ({
        "--storefront-navbar-bg": configuredNavbarBackgroundColor,
        "--storefront-navbar-bg-solid": solidNavbarBackgroundColor,
        "--storefront-navbar-bg-effective": effectiveNavbarBackgroundColor,
        "--storefront-navbar-text":
          navbarSettings?.textColor || "rgba(0, 0, 0, 1)",
        "--storefront-navbar-icon":
          navbarSettings?.iconColor || "rgba(0, 0, 0, 1)",
        "--storefront-navbar-cart-count":
          navbarSettings?.cartCountColor || "rgba(239, 68, 68, 1)",
        "--storefront-navbar-hover":
          navbarSettings?.hoverColor || "rgba(0, 0, 0, 1)",
        "--storefront-navbar-hover-border":
          navbarSettings?.borderHoverColor || "rgba(0, 0, 0, 0.2)",
        "--storefront-navbar-height": `${navbarHeightPx}px`,
      }) as CSSProperties,
    [
      configuredNavbarBackgroundColor,
      effectiveNavbarBackgroundColor,
      navbarHeightPx,
      navbarSettings,
      solidNavbarBackgroundColor,
    ]
  )

  const sideMenuTheme = useMemo<SideMenuTheme>(
    () => ({
      backgroundColor: effectiveNavbarBackgroundColor,
      textColor: navbarSettings?.textColor || "rgba(0, 0, 0, 1)",
      iconColor: navbarSettings?.iconColor || "rgba(0, 0, 0, 1)",
      hoverColor: navbarSettings?.hoverColor || "rgba(0, 0, 0, 1)",
      hoverBorderColor:
        navbarSettings?.borderHoverColor || "rgba(0, 0, 0, 0.2)",
    }),
    [effectiveNavbarBackgroundColor, navbarSettings]
  )

  const baseCategories = useMemo(() => {
    const items = menu?.items ?? []
    const mapped = items
      .map((item) => mapMenuItemToNavLink(item, 0))
      .filter((category): category is NavCategory => Boolean(category))

    if (!mapped.length) {
      return FALLBACK_NAV_CATEGORIES
    }

    return mapped
  }, [menu])

  const announcementMessages = useMemo(() => {
    const rawMessages = Array.isArray(announcementBar?.messages)
      ? announcementBar.messages
      : []
    if (rawMessages.length) {
      return rawMessages
        .slice()
        .sort((a, b) => a.order - b.order)
        .filter((message) => message.message.trim().length)
    }
    if (announcementBar?.message?.trim()) {
      return [
        { id: "legacy", message: announcementBar.message.trim(), order: 0 },
      ]
    }
    return []
  }, [announcementBar])
  const announcementMode =
    announcementBar?.mode === "static" ? "static" : "scroll"
  const staticAnnouncementMessage = announcementMessages[0] ?? null

  const announcementMarqueeGroup = useMemo(
    () => (
      <span className="announcement-marquee-group flex items-center gap-10 pr-10 text-xs font-medium md:text-sm">
        {announcementMessages.map((item, index) => (
          <span
            key={`${item.id}-marquee-${index}`}
            className="shrink-0 whitespace-nowrap"
          >
            <AnnouncementMessageContent message={item.message} />
          </span>
        ))}
      </span>
    ),
    [announcementMessages]
  )

  const [isSearchOpen, setSearchOpen] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const shellRef = useRef<HTMLDivElement | null>(null)

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const openCategory = (index: number) => {
    clearCloseTimeout()
    setHoveredIndex(index)
  }

  const scheduleClose = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredIndex(null)
    }, 150)
  }

  useEffect(() => {
    return () => {
      clearCloseTimeout()
    }
  }, [])

  useEffect(() => {
    if (!isFixed || typeof window === "undefined") {
      setHasScrolledPastTop(false)
      return
    }

    const handleScroll = () => {
      setHasScrolledPastTop(window.scrollY > 0)
    }

    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [isFixed])

  useEffect(() => {
    if (typeof window === "undefined") return
    const root = document.documentElement

    const updateOffset = () => {
      if (!isFixed) {
        root.style.setProperty("--storefront-fixed-nav-offset", "0px")
        return
      }
      const height = shellRef.current?.offsetHeight ?? 0
      root.style.setProperty("--storefront-fixed-nav-offset", `${height}px`)
    }

    updateOffset()

    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== "undefined" && shellRef.current) {
      observer = new ResizeObserver(() => {
        updateOffset()
      })
      observer.observe(shellRef.current)
    }

    window.addEventListener("resize", updateOffset)

    return () => {
      window.removeEventListener("resize", updateOffset)
      observer?.disconnect()
      if (!isFixed) {
        root.style.setProperty("--storefront-fixed-nav-offset", "0px")
      }
    }
  }, [announcementMessages.length, isFixed])

  const renderDesktopSubmenuItems = (
    items: NavMenuLink[],
    rootIndex: number,
    depth: number
  ) => (
    <ul
      className={
        depth === 1
          ? "flex flex-col gap-1"
          : "mt-1 ml-5 flex flex-col gap-1 pl-4"
      }
    >
      {items.map((entry) => (
        <li key={entry.id}>
          {renderMenuLink(entry, {
            className:
              "storefront-nav-link block px-0 py-1.5 text-sm tracking-wide transition-colors duration-200",
            testId: `nav-sub-${toTestId(entry.name)}-${depth}-link`,
            depth,
            onClick: () => setHoveredIndex(null),
            onFocus: () => openCategory(rootIndex),
            onBlur: scheduleClose,
          })}
          {entry.children?.length
            ? renderDesktopSubmenuItems(entry.children, rootIndex, depth + 1)
            : null}
        </li>
      ))}
    </ul>
  )

  const renderDesktopInlineCategories = () => (
    <div className="flex h-full items-center gap-8">
      {baseCategories.map((category, idx) => (
        <div
          key={category.id}
          className="relative"
          onMouseEnter={() => openCategory(idx)}
          onMouseLeave={scheduleClose}
        >
          {renderMenuLink(category, {
            className:
              "storefront-nav-link border-b border-transparent text-[13px] uppercase tracking-[0.15em] font-bold transition-colors duration-200",
            testId: `nav-${toTestId(category.name)}-link`,
            depth: 0,
            onClick: () => setHoveredIndex(null),
            onFocus: () => openCategory(idx),
            onBlur: scheduleClose,
          })}

          {category.children?.length ? (
            <div
              className={`absolute left-0 top-full mt-0 flex z-40 w-64 translate-y-0 transform border-t transition-all duration-200 ease-out ${
                hoveredIndex === idx
                  ? "visible opacity-100"
                  : "invisible opacity-0 pointer-events-none"
              }`}
              style={{
                backgroundColor:
                  "var(--storefront-navbar-bg-effective, #ffffff)",
                borderColor: "rgba(0, 0, 0, 0.08)",
              }}
              onMouseEnter={() => openCategory(idx)}
              onMouseLeave={scheduleClose}
            >
              <div className="w-full px-6 py-5">
                {renderDesktopSubmenuItems(category.children, idx, 1)}
              </div>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  )

  const renderDesktopIcons = () => (
    <div className="flex items-center gap-x-4">
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="storefront-nav-icon-button transition-colors"
        data-testid="nav-search-button"
        aria-label="Abrir búsqueda"
      >
        <MagnifyingGlass className="w-5 h-5" />
      </button>

      <Suspense
        fallback={
          <LocalizedClientLink
            className="storefront-nav-icon-button transition-colors"
            href="/checkout"
            data-testid="nav-cart-link"
          >
            <ShoppingBag className="w-5 h-5" />
          </LocalizedClientLink>
        }
      >
        <CartButton
          iconColor="var(--storefront-navbar-icon, #000)"
          hoverColor="var(--storefront-navbar-hover, #000)"
          cartCountColor="var(--storefront-navbar-cart-count, #ef4444)"
        />
      </Suspense>
    </div>
  )

  return (
    <div className="relative" style={navStyles}>
      <SearchBar isOpen={isSearchOpen} onClose={() => setSearchOpen(false)} />
      <div
        ref={shellRef}
        className={isFixed ? "fixed inset-x-0 top-0 z-[70]" : "relative z-[70]"}
      >
        {announcementBar?.enabled && announcementMessages.length > 0 && (
          <div style={announcementBarStyle}>
            <div className="relative overflow-hidden px-4 py-2">
              {announcementMode === "scroll" ? (
                <MarqueeText
                  className="announcement-marquee"
                  duration={40}
                  direction="right"
                  textSpacing="2.5rem"
                  pauseOnHover={false}
                  playOnlyInView={true}
                  treshold={0.1}
                >
                  {announcementMarqueeGroup}
                </MarqueeText>
              ) : staticAnnouncementMessage ? (
                <div className="flex items-center justify-center text-center text-xs font-medium md:text-sm">
                  <AnnouncementMessageContent
                    message={staticAnnouncementMessage.message}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}
        <header
          className="relative mx-auto border-b transition-[background-color] duration-300 ease-out"
          style={{
            backgroundColor: "var(--storefront-navbar-bg-effective, #ffffff)",
            borderColor: "transparent",
            color: "var(--storefront-navbar-text, #000000)",
          }}
        >
          <nav
            className="content-container txt-xsmall-plus text-ui-fg-subtle relative flex w-full items-center justify-between text-small-regular lg:flex"
            style={{ height: "var(--storefront-navbar-height, 74px)" }}
          >
            <div
              className={`${
                logoPosition === "center" ? "md:hidden" : "lg:hidden"
              } flex items-center justify-between w-full h-full`}
            >
              <div className="flex items-center">
                <SideMenu categories={baseCategories} theme={sideMenuTheme} />
              </div>

              <div className="flex items-center h-full absolute left-1/2 transform -translate-x-1/2">
                <LocalizedClientLink
                  href="/"
                  prefetch
                  className="txt-compact-xlarge-plus uppercase"
                  data-testid="nav-store-link"
                >
                  <StorefrontLogo
                    brandName={storeConfig?.brandName || ""}
                    logoUrl={storeConfig?.logoUrl || ""}
                    width={logoDimensions.width}
                    height={logoDimensions.height}
                    className="h-auto"
                    textClassName="whitespace-nowrap"
                    priority
                  />
                </LocalizedClientLink>
              </div>

              <div className="flex items-center gap-x-4">
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="storefront-nav-icon-button transition-colors"
                  data-testid="nav-search-button"
                  aria-label="Abrir búsqueda"
                >
                  <MagnifyingGlass className="w-5 h-5" />
                </button>

                <Suspense
                  fallback={
                    <LocalizedClientLink
                      className="storefront-nav-icon-button transition-colors"
                      href="/checkout"
                      data-testid="nav-cart-link"
                    >
                      <ShoppingBag className="w-5 h-5" />
                    </LocalizedClientLink>
                  }
                >
                  <CartButton
                    iconColor="var(--storefront-navbar-icon, #000)"
                    hoverColor="var(--storefront-navbar-hover, #000)"
                    cartCountColor="var(--storefront-navbar-cart-count, #ef4444)"
                  />
                </Suspense>
              </div>
            </div>

            {logoPosition === "center" ? (
              <div className="hidden md:flex items-center justify-between w-full h-full">
                <div className="flex items-center relative z-[2]">
                  <SideMenu categories={baseCategories} theme={sideMenuTheme} />
                </div>

                <div className="flex items-center h-full absolute left-1/2 transform -translate-x-1/2 z-[1]">
                  <LocalizedClientLink
                    href="/"
                    prefetch
                    className="txt-compact-xlarge-plus uppercase"
                    data-testid="nav-store-link"
                  >
                    <StorefrontLogo
                      brandName={storeConfig?.brandName || ""}
                      logoUrl={storeConfig?.logoUrl || ""}
                      width={logoDimensions.width}
                      height={logoDimensions.height}
                      className="h-auto"
                      textClassName="whitespace-nowrap"
                      priority
                    />
                  </LocalizedClientLink>
                </div>

                <div className="ml-auto flex items-center justify-end shrink-0 relative z-[2]">
                  {renderDesktopIcons()}
                </div>
              </div>
            ) : (
              <div className="hidden lg:flex items-center justify-between w-full h-full">
                <div className="flex items-center h-full">
                  <LocalizedClientLink
                    href="/"
                    prefetch
                    className="txt-compact-xlarge-plus uppercase"
                    data-testid="nav-store-link"
                  >
                    <StorefrontLogo
                      brandName={storeConfig?.brandName || ""}
                      logoUrl={storeConfig?.logoUrl || ""}
                      width={logoDimensions.width}
                      height={logoDimensions.height}
                      className="h-auto"
                      textClassName="whitespace-nowrap"
                      priority
                    />
                  </LocalizedClientLink>
                </div>

                {renderDesktopInlineCategories()}

                {renderDesktopIcons()}
              </div>
            )}
          </nav>
        </header>
      </div>
    </div>
  )
}
